import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  ContentLayout,
  Container,
  DatePicker,
  FormField,
  Header,
  SpaceBetween,
  StatusIndicator,
  Select,
  Multiselect,
  Table,
} from '@cloudscape-design/components';
import { useEffect, useRef, useState } from 'react';
import { ExportFeedbackCommandOutput } from '@amzn/gbsa-helios-typescript-client';
import { Authorized } from 'src/components/Authorized';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useExportFeedbackMutation } from 'src/data/api/helios-service-ts/feedback/queries/useExportFeedbackMutation';
import useUserProfile from 'src/hooks/useUserProfile';
import { formatDateTime } from 'src/utils/generalUtils';
import { useDivision } from 'src/store/division/useDivision';
import { useTemplates } from 'src/store/templates/useTemplates';

/**
 * Header the backend emits for the created-at column in the export CSV/preview. The value is an
 * ISO-8601 UTC instant, so in the preview table we render it through the shared formatter, which
 * converts to the user's timezone preference (matching how other tables display timestamps).
 */
const EXPORT_CREATED_AT_HEADER = 'Created At';

/**
 * Accessibility strings for the date-range pickers. Cloudscape requires these for screen-reader
 * users to operate the calendar; sharing one object keeps the two pickers consistent.
 */
const DATE_PICKER_ARIA = {
  openCalendarAriaLabel: (selectedDate: string | null) =>
    `Choose date${selectedDate ? `, selected date is ${selectedDate}` : ''}`,
  nextMonthAriaLabel: 'Next month',
  previousMonthAriaLabel: 'Previous month',
  todayAriaLabel: 'Today',
};

/** Feedback cannot be created in the future, so disable future dates in the range filters. */
const disableFutureDates = (date: Date): boolean => date <= new Date();

/**
 * Date-range presets, matching the "Date Range" filter used on the audit/task list screens
 * (past week/month/year + a Custom start/end range).
 */
const DATE_RANGE_OPTIONS = [
  { label: 'Past week', value: 'PAST_WEEK' },
  { label: 'Past month', value: 'PAST_MONTH' },
  { label: 'Past year', value: 'PAST_YEAR' },
  { label: 'Custom', value: 'CUSTOM' },
];

/**
 * Resolves the selected date-range option into inclusive created-at bounds. Presets are relative to
 * now; "Custom" uses the start/end pickers (start of the from-day .. end of the to-day, UTC, matching
 * the stored createdAt). Returns empty bounds (all time) when no range is selected.
 */
const resolveDateBounds = (
  type: string | null,
  startDate: string,
  endDate: string,
): { createdAfter?: Date; createdBefore?: Date } => {
  if (!type) {
    return {};
  }
  if (type === 'CUSTOM') {
    return {
      createdAfter: startDate ? new Date(`${startDate}T00:00:00.000Z`) : undefined,
      createdBefore: endDate ? new Date(`${endDate}T23:59:59.999Z`) : undefined,
    };
  }
  const now = new Date();
  const from = new Date(now);
  if (type === 'PAST_WEEK') {
    from.setDate(from.getDate() - 7);
  } else if (type === 'PAST_MONTH') {
    from.setMonth(from.getMonth() - 1);
  } else if (type === 'PAST_YEAR') {
    from.setFullYear(from.getFullYear() - 1);
  }
  return { createdAfter: from, createdBefore: now };
};

/**
 * Formats a generation timestamp into a short "x minutes/hours ago" string so admins understand
 * the export is a recent snapshot (feedback data is refreshed at most every 12 hours).
 */
const formatGeneratedAgo = (generatedAt?: Date): string => {
  if (!generatedAt) {
    return '';
  }
  const diffMs = Date.now() - generatedAt.getTime();
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));
  if (diffMinutes < 1) {
    return 'just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
};

/**
 * Formats the download-link expiry into a short sentence, driven off the server-provided
 * `expiresAt`. The presigned URL is short-lived (the service keeps its lifetime below the signing
 * credential's rotation window), so this must reflect the actual URL expiry rather than the
 * server-side data freshness window.
 */
const formatExpiry = (expiresAt?: Date): string => {
  if (!expiresAt) {
    return 'Download link expires shortly.';
  }
  const diffMs = expiresAt.getTime() - Date.now();
  if (diffMs <= 0) {
    return 'Download link has expired — generate again for a fresh link.';
  }
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 60) {
    return `Download link expires in ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}.`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  return `Download link expires in ${diffHours} hour${diffHours === 1 ? '' : 's'}.`;
};

/**
 * Admin page for exporting business-unit-scoped feedback.
 *
 * The page is permission gated (FEEDBACK.EXPORT_FEEDBACK). Admins can optionally narrow the export
 * by division and by a created-date range. Generating triggers a server-side scan + CSV build and
 * returns a short-lived presigned S3 download link; no feedback rows are loaded into the browser.
 * Exports are cached server-side per filter combination, so repeated requests within the freshness
 * window return the same recent snapshot.
 */
const FeedbackExportPage = () => {
  const { getUserBusinessUnitId, getUserProgramId, currentUser } = useUserProfile();
  const businessUnitId = getUserBusinessUnitId();

  const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
    useDivision();
  const { templatesList, fetchTemplatesApiStatus, loadTemplatesList } = useTemplates();

  const [divisions, setDivisions] = useState<string[]>([]);
  const [templateNames, setTemplateNames] = useState<string[]>([]);
  const [dateRangeType, setDateRangeType] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [result, setResult] = useState<ExportFeedbackCommandOutput | undefined>();

  // Load the business unit's divisions and templates for the optional filters.
  useEffect(() => {
    if (businessUnitId) {
      loadDivisionsByBusinessUnit({
        businessUnitId,
        programId: getUserProgramId(),
        isDeleted: false,
        isActive: true,
      });
      loadTemplatesList({ businessUnitId, isActive: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId, currentUser]);

  const { mutate, isPending, isError, error } = useExportFeedbackMutation(
    businessUnitId,
    {
      onSuccess: (output) => setResult(output),
    },
  );

  // A start date later than the end date is invalid (only relevant for a custom range).
  const isCustomRange = dateRangeType === 'CUSTOM';
  const invalidRange = isCustomRange && !!startDate && !!endDate && startDate > endDate;
  // A custom range with no dates at all is a no-op (behaves like "all time"), which is misleading —
  // require at least one bound before allowing generation.
  const customRangeIncomplete = isCustomRange && !startDate && !endDate;

  // Whether any optional filter is applied — drives the "Clear filters" affordance and the
  // empty-state wording, consistent with the filter/list pages elsewhere in the app.
  const hasActiveFilters =
    divisions.length > 0 || templateNames.length > 0 || !!dateRangeType;

  const resultRef = useRef<HTMLDivElement>(null);

  // Bring the result into view once it arrives so the outcome isn't missed below the filters.
  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }
  }, [result]);

  const clearFilters = () => {
    setDivisions([]);
    setTemplateNames([]);
    setDateRangeType(null);
    setStartDate('');
    setEndDate('');
  };

  const handleGenerate = () => {
    setResult(undefined);
    const { createdAfter, createdBefore } = resolveDateBounds(
      dateRangeType,
      startDate,
      endDate,
    );
    mutate({
      businessUnitId,
      divisionIds: divisions.length ? divisions : undefined,
      templateNames: templateNames.length ? templateNames : undefined,
      createdAfter,
      createdBefore,
    });
  };

  return (
    <Authorized
      permissionsToCheck={[permissionAccessConfig.FEEDBACK.EXPORT_FEEDBACK]}
      unauthorizedComponent={
        <ContentLayout header={<Header variant="h1">Feedback</Header>}>
          <Alert type="error" header="Access denied">
            You do not have permission to export feedback. Please contact your
            administrator.
          </Alert>
        </ContentLayout>
      }
    >
      <ContentLayout
        header={
          <Header
            variant="h1"
            description="Generate a CSV export of feedback. Optionally filter by division, template, and date range."
          >
            Feedback
          </Header>
        }
      >
        <Container>
          <SpaceBetween size="m">
            <ColumnLayout columns={4}>
              <FormField label="Division">
                <Multiselect
                  data-testid="feedback-export-division-select"
                  selectedOptions={divisions.map((d) => ({ label: d, value: d }))}
                  onChange={({ detail }) =>
                    setDivisions(
                      detail.selectedOptions.map((o) => o.value ?? '').filter(Boolean),
                    )
                  }
                  options={(divisionList ?? []).map((d) => ({
                    label: d.divisionName,
                    value: d.divisionName,
                  }))}
                  statusType={fetcDivisionsApiStatus.isLoading ? 'loading' : 'finished'}
                  loadingText="Loading divisions"
                  placeholder="All divisions"
                  filteringType="auto"
                  empty="No divisions"
                />
              </FormField>
              <FormField label="Template">
                <Multiselect
                  data-testid="feedback-export-template-select"
                  selectedOptions={templateNames.map((t) => ({ label: t, value: t }))}
                  onChange={({ detail }) =>
                    setTemplateNames(
                      detail.selectedOptions.map((o) => o.value ?? '').filter(Boolean),
                    )
                  }
                  options={(templatesList ?? []).map((t) => ({
                    label: t.templateName,
                    value: t.templateName,
                  }))}
                  statusType={fetchTemplatesApiStatus.isLoading ? 'loading' : 'finished'}
                  loadingText="Loading templates"
                  placeholder="All templates"
                  filteringType="auto"
                  empty="No templates"
                />
              </FormField>
              <FormField label="Date Range">
                <Select
                  data-testid="feedback-export-date-range-select"
                  selectedOption={
                    dateRangeType
                      ? DATE_RANGE_OPTIONS.find((o) => o.value === dateRangeType)!
                      : null
                  }
                  onChange={({ detail }) =>
                    setDateRangeType(detail.selectedOption?.value ?? null)
                  }
                  options={DATE_RANGE_OPTIONS}
                  placeholder="All time"
                />
              </FormField>
              {isCustomRange && (
                <>
                  <FormField
                    label="Start Date"
                    constraintText={
                      customRangeIncomplete
                        ? 'Select a start and/or end date to apply a custom range.'
                        : undefined
                    }
                  >
                    <DatePicker
                      data-testid="feedback-export-start-date"
                      value={startDate}
                      onChange={({ detail }) => setStartDate(detail.value)}
                      placeholder="YYYY/MM/DD"
                      isDateEnabled={disableFutureDates}
                      {...DATE_PICKER_ARIA}
                    />
                  </FormField>
                  <FormField
                    label="End Date"
                    errorText={
                      invalidRange
                        ? 'End date must be on or after the start date.'
                        : undefined
                    }
                  >
                    <DatePicker
                      data-testid="feedback-export-end-date"
                      value={endDate}
                      onChange={({ detail }) => setEndDate(detail.value)}
                      placeholder="YYYY/MM/DD"
                      isDateEnabled={disableFutureDates}
                      {...DATE_PICKER_ARIA}
                    />
                  </FormField>
                </>
              )}
            </ColumnLayout>

            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="primary"
                loading={isPending}
                disabled={
                  !businessUnitId || isPending || invalidRange || customRangeIncomplete
                }
                onClick={handleGenerate}
                data-testid="generate-feedback-export-button"
              >
                {isPending ? 'Generating export...' : 'Generate export'}
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="link"
                  disabled={isPending}
                  onClick={clearFilters}
                  data-testid="clear-filters-button"
                >
                  Clear filters
                </Button>
              )}
            </SpaceBetween>

            {isError && (
              <Alert type="error" header="Unable to generate export">
                {error?.message ||
                  'An error occurred while generating the feedback export. Please try again.'}
              </Alert>
            )}

            {result && (
              <div ref={resultRef}>
                <SpaceBetween size="m">
                  {(result.recordCount ?? 0) === 0 && (
                    <Alert
                      type="info"
                      header="No feedback to export"
                      action={
                        hasActiveFilters && (
                          <Button
                            onClick={clearFilters}
                            data-testid="empty-clear-filters-button"
                          >
                            Clear filters
                          </Button>
                        )
                      }
                    >
                      {hasActiveFilters
                        ? 'No feedback matched your filters. Try widening the date range or division, or clear the filters.'
                        : 'No feedback has been submitted for your business unit yet.'}
                    </Alert>
                  )}

                  {(result.recordCount ?? 0) > 0 && (
                    <Alert type="success" header="Export ready">
                      <SpaceBetween size="xs">
                        <StatusIndicator type="success">
                          {`${result.recordCount ?? 0} feedback record${
                            result.recordCount === 1 ? '' : 's'
                          } included`}
                        </StatusIndicator>
                        <Box variant="small" color="text-body-secondary">
                          {`Generated ${formatGeneratedAgo(result.generatedAt)}. ${formatExpiry(
                            result.expiresAt,
                          )}`}
                        </Box>
                        <Button
                          iconName="download"
                          href={result.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="download-feedback-export-link"
                        >
                          Download CSV
                        </Button>
                      </SpaceBetween>
                    </Alert>
                  )}

                  {result.previewRows && result.previewRows.length > 0 && (
                    <Table
                      data-testid="feedback-export-preview"
                      variant="embedded"
                      header={
                        <Header
                          variant="h3"
                          counter={
                            (result.recordCount ?? 0) > result.previewRows.length
                              ? `(first ${result.previewRows.length} of ${result.recordCount})`
                              : `(${result.previewRows.length})`
                          }
                        >
                          Preview
                        </Header>
                      }
                      columnDefinitions={(result.previewHeaders ?? []).map(
                        (heading: string, index: number) => {
                          const isCreatedAt = heading === EXPORT_CREATED_AT_HEADER;
                          return {
                            id: `col-${index}`,
                            header: heading,
                            cell: (row: string[]) => {
                              const value = row[index] ?? '';
                              // Convert the UTC instant to the user's timezone, like other tables.
                              return isCreatedAt && value ? formatDateTime(value) : value;
                            },
                          };
                        },
                      )}
                      items={result.previewRows}
                      empty="No rows to preview"
                    />
                  )}
                </SpaceBetween>
              </div>
            )}
          </SpaceBetween>
        </Container>
      </ContentLayout>
    </Authorized>
  );
};

export default FeedbackExportPage;
